/**
 * Postcode suggestions as the engineer types, from postcodes.io — the same
 * source and settings as job setup and the Change Location drawer. Any
 * failure just means no suggestions (searchPostcodes never throws).
 */
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from './useDebounce'
import { normalizePostcode, searchPostcodes } from '../services/postcodes'

const SUGGESTION_MIN_LENGTH = 2
const SUGGESTION_DEBOUNCE_MS = 300

/**
 * @param query - What's in the postcode field.
 * @param enabled - Only fetch while the suggestion list can be shown.
 */
export function usePostcodeSuggestions(query: string, enabled: boolean): string[] {
  const debouncedQuery = useDebounce(query.trim(), SUGGESTION_DEBOUNCE_MS)
  const { data: suggestions = [] } = useQuery({
    queryKey: ['postcode-autocomplete', debouncedQuery],
    queryFn: () => searchPostcodes(debouncedQuery),
    enabled: enabled && debouncedQuery.length >= SUGGESTION_MIN_LENGTH,
    staleTime: 30000,
    // Keep previous results while the next ones load, so the list doesn't flicker
    placeholderData: (previousData) => previousData,
  })
  if (!enabled) return []
  // Results can be for an older query: the debounce hasn't caught up yet, or
  // the previous list is kept while the next one loads. Only offer
  // suggestions that still start with what's in the field, so changing "SO"
  // to "M" never leaves SO postcodes on screen to be tapped.
  const typed = normalizePostcode(query)
  if (typed.length < SUGGESTION_MIN_LENGTH) return []
  return suggestions.filter((suggestion) => normalizePostcode(suggestion).startsWith(typed))
}
